import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useCallback, useRef, useState } from 'react';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, IconButton, InputAdornment, Alert, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import Turnstile from '../../../components/Turnstile';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';
// config
import { EVENT_ID, INPUT_ID } from '../../../config';

// ----------------------------------------------------------------------

export default function RegisterForm() {
  const { register } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const turnstileRef = useRef(null);

  const [turnstileToken, setTurnstileToken] = useState('');

  // 위젯이 죽었을 때(스크립트 차단, Cloudflare 장애) 가입 자체가 막히지 않도록 제출을 다시 허용한다.
  // 백엔드도 같은 이유로 fail-open 이다.
  const [turnstileFailed, setTurnstileFailed] = useState(false);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
    setTurnstileFailed(false);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    // 만료된 토큰은 재사용할 수 없다. 위젯을 리셋해 새 토큰을 받는다 (register.md §3).
    setTurnstileToken('');
    turnstileRef.current?.reset();
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken('');
    setTurnstileFailed(true);
  }, []);

  const RegisterSchema = Yup.object().shape({
    email: Yup.string().trim().email('이메일을 입력하세요').required('이메일을 입력하세요'),
    // 서버 정책(AUTH_PASSWORD_VALIDATORS)과 같은 문구를 쓴다. 어느 쪽에서 걸리든 안내가 같아야 한다.
    // '흔한 비밀번호'와 '이메일과 유사'는 클라이언트에서 판정할 수 없어 서버 응답에 맡긴다.
    password: Yup.string()
      .trim()
      .required('비밀번호를 입력하세요')
      .min(8, '비밀번호는 8자 이상이어야 합니다.')
      .max(50, '비밀번호는 50자 이하여야 합니다.')
      .matches(/\D/, { message: '숫자로만 만들 수 없습니다. 영문자를 함께 사용해주세요.', excludeEmptyString: true }),
  });

  const defaultValues = {
    email: '',
    password: '',
    allowMarketing: false,
  };

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await register(data.email, data.password, data.allowMarketing, turnstileToken);
      enqueueSnackbar('인증 메일을 발송하였습니다', { variant: 'info' })
    } catch (error) {
      console.error(error);

      // 토큰은 1회용이다. 실패 원인과 무관하게 위젯을 리셋해야 재시도가 가능하다.
      turnstileRef.current?.reset();
      setTurnstileToken('');

      const { status, data: errorData } = error.response || {};

      if (status === 409) {
        setError('afterSubmit', { type: "manual", message: "이미 가입된 이메일입니다" });
      } else if (status === 400 && errorData?.message) {
        // 봇 검증 실패와 비밀번호 정책 위반이 모두 이 형태로 온다. 서버가 한국어 문구를 주므로
        // 그대로 노출하고, 입력값은 유지한다 (필드별 에러로 주면 폼이 초기화되는 것을 서버 쪽에서 피한 설계).
        setError('afterSubmit', { type: "manual", message: errorData.message });
      } else {
        reset();
        if (isMountedRef.current) {
          setError('afterSubmit', { ...error, message: error.message });
        }
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label="이메일" id={INPUT_ID.registerEmail} />

        <RHFTextField
          name="password"
          label="비밀번호"
          helperText="8자 이상 50자 이하, 숫자로만 구성할 수 없습니다"
          id={INPUT_ID.registerPassword}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" alignItems={"center"} justifyContent={"space-between"}>
          <RHFCheckbox
            name="allowMarketing"
            label="맞춤 서비스 제안 등 마케팅 정보수신"
          />
          <Link href={"https://spacey-dio.notion.site/DIO-122b0a107b154feb886dcec094a70940"} target="_blank" rel="noopener" sx={{ height: '20px' }}>
            <Iconify icon={'ic:baseline-chevron-right'} sx={{ width: '20px', height: '20px' }} />
          </Link>
        </Stack>

        <Turnstile
          ref={turnstileRef}
          onVerify={handleTurnstileVerify}
          onExpire={handleTurnstileExpire}
          onError={handleTurnstileError}
        />

        {turnstileFailed && (
          <Alert severity="warning">
            보안 확인을 불러오지 못했습니다. 새로고침 후에도 같으면 다른 브라우저나 네트워크에서 시도해 주세요.
          </Alert>
        )}

        <LoadingButton
          id={EVENT_ID.crewRegister}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={!turnstileToken && !turnstileFailed}
        >
          크루 지원하기
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}
